import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AgxSpacer, AgxTextArea, AgxTextInput } from '@urbanx/agx.ui-components2';
import Card from 'components/card/Card';
import { AgxToast } from '@urbanx/agx-ui-components';
import {
  CampaignDetailModel,
  StaffGetPhotosAndMediaDetailsForCampaignModel,
  UploadedDocument,
  UploadedDocumentDocumentType,
  useInvalidateUseStaffGetPhotosAndMediaDetailsForCampaign,
  useStaffGetPhotosAndMediaDetailsForCampaign,
  useStaffUpdatePhotosAndMediaDetailsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { AgxToastState } from 'types/commonTypes';
import { MediaLinkType, MediaLinksValidator, ValidationErrors } from './PhotosAndMediaHelper';
import { AuthContext } from '@urbanx/agx.auth.client';
import { CampaignImageUploader } from './CampaignImageUploader';

interface PhotosAndMediaProps {
  campaign?: CampaignDetailModel;
  onSaveStateChange: (state: {
    saveChanges: (() => void) | undefined;
    isFormDirty: boolean;
    isFormValid: boolean;
    isPending: boolean;
    showSavedMessage: boolean;
  }) => void;
}

const headlineMaxLength = 150;
const descriptionMaxLength = 20000;

const PhotosAndMedia = ({ campaign, onSaveStateChange }: PhotosAndMediaProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [photosAndMediaDetails, setPhotosAndMediaDetails] =
    useState<StaffGetPhotosAndMediaDetailsForCampaignModel | null>(null);
  const [isPhotosBusy, setIsPhotosBusy] = useState(false);
  const [isFloorPlansBusy, setIsFloorPlansBusy] = useState(false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const initialLoadDoneRef = useRef(false);
  const { getAuthToken } = useContext(AuthContext);

  const invalidateStaffGetPhotosAndMediaDetails =
    useInvalidateUseStaffGetPhotosAndMediaDetailsForCampaign();

  const { data: campaignPhotosAndMediaDetails } = useStaffGetPhotosAndMediaDetailsForCampaign(
    campaign?.campaignId || '',
    !!campaign?.campaignId
  );

  const headlineRemaining =
    headlineMaxLength - (photosAndMediaDetails?.listingDescription?.headline?.length || 0);
  const descriptionRemaining =
    descriptionMaxLength - (photosAndMediaDetails?.listingDescription?.description?.length || 0);

  const { staffUpdatePhotosAndMediaDetailsForCampaign } =
    useStaffUpdatePhotosAndMediaDetailsForCampaign({
      onSuccess: async () => {
        await invalidateStaffGetPhotosAndMediaDetails(campaign?.campaignId || '');
        initialLoadDoneRef.current = false;
        setIsFormDirty(false);
        setShowSavedMessage(true);

        updateToastState({
          color: 'success',
          message: 'Listing details updated successfully',
          open: true,
        });

        onSaveStateChange({
          saveChanges,
          isFormDirty: false,
          isFormValid: true,
          isPending: false,
          showSavedMessage: true,
        });
      },
      onError: (error) => {
        console.error('Error updating listing details:', error);
        updateToastState({
          color: 'error',
          message: 'Error updating listing details',
          open: true,
        });

        onSaveStateChange({
          saveChanges,
          isFormDirty: false,
          isFormValid: true,
          isPending: false,
          showSavedMessage: true,
        });
      },
    });

  useEffect(() => {
    if (campaignPhotosAndMediaDetails && !initialLoadDoneRef.current) {
      setPhotosAndMediaDetails({
        ...campaignPhotosAndMediaDetails,
        photos: campaignPhotosAndMediaDetails.photos?.map((d) => ({ ...d })) || [],
        floorPlans: campaignPhotosAndMediaDetails.floorPlans?.map((d) => ({ ...d })) || [],
        listingDescription: {
          ...campaignPhotosAndMediaDetails.listingDescription,
        },
        mediaLinks: { ...campaignPhotosAndMediaDetails.mediaLinks },
      });
      setIsFormDirty(false);
      setValidationErrors({});
      initialLoadDoneRef.current = true;
    }
  }, [campaignPhotosAndMediaDetails]);

  // Check if form is dirty
  useEffect(() => {
    if (!initialLoadDoneRef.current || !photosAndMediaDetails || !campaignPhotosAndMediaDetails)
      return;

    const hasImageOrderChanged = (
      updatedImages: UploadedDocument[] | undefined,
      existingImages: UploadedDocument[] | undefined
    ) => {
      const getIds = (images: UploadedDocument[] | undefined) =>
        (images || []).map((d) => d.documentId);
      return JSON.stringify(getIds(updatedImages)) !== JSON.stringify(getIds(existingImages));
    };

    const dirty =
      photosAndMediaDetails.listingDescription?.headline !==
        campaignPhotosAndMediaDetails.listingDescription?.headline ||
      photosAndMediaDetails.listingDescription?.description !==
        campaignPhotosAndMediaDetails.listingDescription?.description ||
      photosAndMediaDetails.mediaLinks?.videoLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.videoLink ||
      photosAndMediaDetails.mediaLinks?.virtualTourLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.virtualTourLink ||
      photosAndMediaDetails.mediaLinks?.externalLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.externalLink ||
      hasImageOrderChanged(photosAndMediaDetails.photos, campaignPhotosAndMediaDetails.photos) ||
      hasImageOrderChanged(
        photosAndMediaDetails.floorPlans,
        campaignPhotosAndMediaDetails.floorPlans
      );

    setIsFormDirty(dirty);
  }, [photosAndMediaDetails, campaignPhotosAndMediaDetails]);

  const validateLink = useCallback((type: MediaLinkType, value: string) => {
    const error = MediaLinksValidator.validateMediaLinkType(type, value);
    setValidationErrors((prev) => ({ ...prev, [type]: error }));
    return !error;
  }, []);

  const updatePhotosAndMediaDetails = useCallback(
    (updates: Partial<StaffGetPhotosAndMediaDetailsForCampaignModel>) => {
      setPhotosAndMediaDetails((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ...updates,
          listingDescription: {
            ...prev.listingDescription,
            ...updates.listingDescription,
          },
          mediaLinks: { ...prev.mediaLinks, ...updates.mediaLinks },
        };
      });
      if (showSavedMessage) setShowSavedMessage(false);
    },
    [showSavedMessage]
  );

  const saveChanges = useCallback(() => {
    if (!campaign?.campaignId) {
      updateToastState({
        color: 'error',
        message: 'Campaign ID is required',
        open: true,
      });
      return;
    }

    const allValidationErrors = MediaLinksValidator.validateAllLinks({
      videoLink: photosAndMediaDetails?.mediaLinks?.videoLink,
      virtualTourLink: photosAndMediaDetails?.mediaLinks?.virtualTourLink,
      externalLink: photosAndMediaDetails?.mediaLinks?.externalLink,
    });

    if (MediaLinksValidator.hasValidationErrors(allValidationErrors)) {
      setValidationErrors(allValidationErrors);
      updateToastState({
        color: 'error',
        message: 'Please fix validation errors before saving',
        open: true,
      });
      return;
    }

    staffUpdatePhotosAndMediaDetailsForCampaign({
      campaignId: campaign.campaignId,
      listingDescription: photosAndMediaDetails?.listingDescription,
      mediaLinks: photosAndMediaDetails?.mediaLinks,
      photos: photosAndMediaDetails?.photos,
      floorPlans: photosAndMediaDetails?.floorPlans,
    });

    onSaveStateChange({
      saveChanges,
      isFormDirty: isFormDirty && !hasValidationErrors && !isBusy,
      isFormValid: !hasValidationErrors,
      isPending: true,
      showSavedMessage,
    });
  }, [campaign?.campaignId, photosAndMediaDetails, staffUpdatePhotosAndMediaDetailsForCampaign]);

  const hasValidationErrors = MediaLinksValidator.hasValidationErrors(validationErrors);
  const isBusy = isPhotosBusy || isFloorPlansBusy;

  useEffect(() => {
    onSaveStateChange({
      saveChanges,
      isFormDirty: isFormDirty && !hasValidationErrors && !isBusy,
      isFormValid: !hasValidationErrors,
      isPending: false,
      showSavedMessage,
    });
  }, [saveChanges, isFormDirty, hasValidationErrors, isBusy, showSavedMessage, onSaveStateChange]);

  return (
    <>
      <AgxToast selector="#agxToast" toastState={toastState} />
      <AgxSpacer direction="vertical" gap="spacing-2xl" padding={{ top: 'spacing-3xl' }} fill>
        <AgxSpacer direction="vertical" fill gap="spacing-3xl">
          {/* Photos */}
          <Card title="Photos" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaPhotosPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <CampaignImageUploader
                campaignId={campaign?.campaignId || ''}
                documentType={UploadedDocumentDocumentType.ListingPropertyImage}
                documents={photosAndMediaDetails?.photos}
                maxImages={35}
                getAuthToken={getAuthToken}
                onDocumentsChange={(updated) => updatePhotosAndMediaDetails({ photos: updated })}
                onToast={({ color, message }) => updateToastState({ color, message, open: true })}
                onBusyStateChange={setIsPhotosBusy}
                id="listing-image-uploader"
                dataTestId="uploader-listing"
              />
            </AgxSpacer>
          </Card>

          {/* Floor Plan */}
          <Card title="Floor Plan" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaFloorPlanPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <CampaignImageUploader
                campaignId={campaign?.campaignId || ''}
                documentType={UploadedDocumentDocumentType.ListingFloorPlan}
                documents={photosAndMediaDetails?.floorPlans}
                maxImages={2}
                getAuthToken={getAuthToken}
                onDocumentsChange={(updated) =>
                  updatePhotosAndMediaDetails({ floorPlans: updated })
                }
                onToast={({ color, message }) => updateToastState({ color, message, open: true })}
                onBusyStateChange={setIsFloorPlansBusy}
                id="floorplan-image-uploader"
                dataTestId="uploader-floorplan"
              />
            </AgxSpacer>
          </Card>

          {/* Listing Description */}
          <Card title="Listing Description" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaDescriptionPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <AgxTextArea
                id="headlineTextarea"
                label="Headline"
                placeholder="Enter headline"
                optionalText={`${headlineRemaining} characters left`}
                defaultValue={photosAndMediaDetails?.listingDescription?.headline || ''}
                onInputValueChange={({ value }) => {
                  updatePhotosAndMediaDetails({
                    listingDescription: {
                      ...photosAndMediaDetails?.listingDescription,
                      headline: value,
                    },
                  });
                }}
                rows={5}
                maxLength={headlineMaxLength}
                stretch
              />
              <AgxTextArea
                id="descriptionTextarea"
                label="Description"
                placeholder="Enter description"
                optionalText={`${descriptionRemaining.toLocaleString()} characters left`}
                defaultValue={photosAndMediaDetails?.listingDescription?.description || ''}
                onInputValueChange={({ value }) => {
                  updatePhotosAndMediaDetails({
                    listingDescription: {
                      ...photosAndMediaDetails?.listingDescription,
                      description: value,
                    },
                  });
                }}
                rows={15}
                maxLength={descriptionMaxLength}
                stretch
              />
            </AgxSpacer>
          </Card>

          {/* Media Links */}
          <Card title="Media Links" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaMediaLinksPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <AgxTextInput
                id="videoLinkInput"
                label="Video"
                labelHintText="YouTube link only"
                type="text"
                onChange={(value) => {
                  updatePhotosAndMediaDetails({
                    mediaLinks: {
                      ...photosAndMediaDetails?.mediaLinks,
                      videoLink: value,
                    },
                  });
                  validateLink(MediaLinkType.VIDEO_LINK, value);
                }}
                defaultValue={photosAndMediaDetails?.mediaLinks?.videoLink || ''}
                stretch
                errorText={validationErrors[MediaLinkType.VIDEO_LINK]}
              />
              <AgxTextInput
                id="virtualTourInput"
                label="Virtual Tour"
                labelHintText="Matterport link only"
                type="text"
                onChange={(value) => {
                  updatePhotosAndMediaDetails({
                    mediaLinks: {
                      ...photosAndMediaDetails?.mediaLinks,
                      virtualTourLink: value,
                    },
                  });
                  validateLink(MediaLinkType.VIRTUAL_TOUR_LINK, value);
                }}
                defaultValue={photosAndMediaDetails?.mediaLinks?.virtualTourLink || ''}
                stretch
                errorText={validationErrors[MediaLinkType.VIRTUAL_TOUR_LINK]}
              />
              <AgxTextInput
                id="externalLinkInput"
                label="External Link"
                labelHintText="Website link"
                type="text"
                onChange={(value) => {
                  updatePhotosAndMediaDetails({
                    mediaLinks: {
                      ...photosAndMediaDetails?.mediaLinks,
                      externalLink: value,
                    },
                  });
                  validateLink(MediaLinkType.EXTERNAL_LINK, value);
                }}
                defaultValue={photosAndMediaDetails?.mediaLinks?.externalLink || ''}
                stretch
                errorText={validationErrors[MediaLinkType.EXTERNAL_LINK]}
              />
            </AgxSpacer>
          </Card>
        </AgxSpacer>
      </AgxSpacer>
    </>
  );
};

export default PhotosAndMedia;
